import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { isEmpty, map } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import isEmail from 'validator/lib/isEmail'
import { FieldValidationError, User, UserCreatePayload, UserRole } from '../../../api/models'
import CustomDialog from '../../../components/dialogs/CustomDialog/CustomDialog'
import { getRoleLabel } from '../../../utils/permissions'
import styles from './CreateUserModal.module.css'

const isFullName = (value: string) => value.trim().split(/\s+/).length >= 2

interface CreateUserModalProps {
  open: boolean
  setClose: () => void
  primaryAction: (data: UserCreatePayload) => void
  ouOptions: string[]
  errors: FieldValidationError[]
  resetErrors: (field?: string) => void
  selectedUser: User | null
}

const CreateUserModal = ({
  open,
  setClose,
  primaryAction,
  ouOptions,
  errors,
  resetErrors,
  selectedUser,
}: CreateUserModalProps) => {
  const [name, setName] = useState(selectedUser?.name ?? '')
  const [email, setEmail] = useState(selectedUser?.email ?? '')
  const [organizationName, setOrganizationName] = useState(selectedUser?.organization_name ?? '')
  const [ou, setOu] = useState(selectedUser?.ou ?? '')
  const [functionField, setFunctionField] = useState(selectedUser?.function ?? '')
  const [role, setRole] = useState<UserRole | string>(selectedUser?.role ?? '')
  const [emptyFieldErrors, setEmptyFieldErrors] = useState<{ field: string; message: string }[]>([])
  const isEditingUser = useMemo(() => {
    return !isEmpty(selectedUser)
  }, [selectedUser])

  const getFieldError = (field: string) => {
    return errors.find(error => error.field === field)
  }

  useEffect(() => {
    if (!open) {
      setName('')
      setEmail('')
      setOrganizationName('')
      setOu('')
      setFunctionField('')
      setRole('')
      setEmptyFieldErrors([])
      resetErrors()
    }
  }, [open])

  useEffect(() => {
    if (isEditingUser) {
      setName(selectedUser?.name ?? '')
      setEmail(selectedUser?.email ?? '')
      setOrganizationName(selectedUser?.organization_name ?? '')
      setOu(selectedUser?.ou ?? '')
      setFunctionField(selectedUser?.function ?? '')
      setRole(selectedUser?.role ?? '')
      setEmptyFieldErrors([])
    }
  }, [isEditingUser, selectedUser])

  const handlePrimaryAction = () => {
    if (!isFullName(name) || !isEmail(email)) return
    primaryAction({
      name: name,
      email: email,
      organization_name: organizationName,
      function: functionField,
      ou: ou,
      role: role as UserRole,
    })
  }
  const getEmptyFieldError = (field: string) => {
    return emptyFieldErrors.find(error => error.field === field)
  }

  const validateField = (value: string, field: string) => {
    setEmptyFieldErrors(prev => prev.filter(error => error.field !== field))
    if (isEmpty(value)) {
      setEmptyFieldErrors(prev => [...prev, { field: field, message: 'This field is mandatory' }])
    } else if (field === 'name' && !isFullName(value)) {
      setEmptyFieldErrors(prev => [...prev, { field: field, message: 'Insert full name' }])
    } else if (field === 'email' && !isEmail(value)) {
      setEmptyFieldErrors(prev => [...prev, { field: field, message: 'Invalid email format' }])
    }
  }

  return (
    <CustomDialog
      isRequestDialog
      open={open}
      setClose={setClose}
      title={isEditingUser ? 'Edit User' : 'Add New User'}
      primaryAction={handlePrimaryAction}
      primaryActionButtonText={isEditingUser ? 'Save' : 'Add'}
      secondaryAction={setClose}
      secondaryActionButtonText="Cancel"
      primaryActionDisabled={
        isEmpty(name) ||
        isEmpty(email) ||
        isEmpty(organizationName) ||
        isEmpty(ou) ||
        isEmpty(functionField) ||
        isEmpty(role) ||
        !isFullName(name) ||
        !isEmail(email)
      }>
      <div className={styles.experimenterRequestDialog}>
        <TextField
          error={!!getFieldError('name') || !!getEmptyFieldError('name')}
          helperText={getFieldError('name')?.message || getEmptyFieldError('name')?.message}
          size="small"
          placeholder="Enter full name"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          label="Full Name"
          value={name}
          onChange={e => {
            resetErrors('name')
            setName(e.target.value)
          }}
          disabled={isEditingUser}
          onBlur={() => {
            validateField(name, 'name')
          }}
        />
        <TextField
          error={!!getFieldError('email') || !!getEmptyFieldError('email')}
          helperText={getFieldError('email')?.message || getEmptyFieldError('email')?.message}
          size="small"
          placeholder="Enter email ID"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          label="Email ID"
          value={email}
          onChange={e => {
            resetErrors('email')
            setEmail(e.target.value)
          }}
          disabled={isEditingUser}
          onBlur={() => {
            validateField(email, 'email')
          }}
        />
        <TextField
          error={!!getFieldError('organization_name') || !!getEmptyFieldError('organization_name')}
          helperText={getFieldError('organization_name')?.message || getEmptyFieldError('organization_name')?.message}
          size="small"
          placeholder="Enter the organization name"
          label="Organization Name"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          value={organizationName}
          onChange={e => setOrganizationName(e.target.value)}
          onBlur={() => {
            validateField(organizationName, 'organization_name')
          }}
        />
        <TextField
          error={!!getFieldError('function') || !!getEmptyFieldError('function')}
          helperText={getFieldError('function')?.message || getEmptyFieldError('function')?.message}
          size="small"
          placeholder="Enter function"
          label="Function"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          value={functionField}
          onChange={e => setFunctionField(e.target.value)}
          onBlur={() => {
            validateField(functionField, 'function')
          }}
        />
        <div className={styles.experimenterRequestDialogSelectsContainer}>
          <FormControl
            style={{ width: '220px' }}
            size="small">
            <InputLabel shrink>OU</InputLabel>
            <Select
              onBlur={() => {
                validateField(ou, 'ou')
              }}
              error={!!getEmptyFieldError('ou')}
              displayEmpty
              renderValue={(value: unknown) => {
                if (isEmpty(value)) {
                  return <p className={styles.experimenterRequestDialogTextFieldFilledLabel}>Select</p>
                }
                return <>{value}</>
              }}
              value={ou}
              onChange={e => setOu(e.target.value as string)}
              label={'OU'}
              size="small">
              {map(ouOptions, option => (
                <MenuItem
                  key={option}
                  value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {getEmptyFieldError('ou')?.message && (
              <FormHelperText error>{getEmptyFieldError('ou')?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl
            style={{ width: '220px' }}
            size="small">
            <InputLabel shrink>Role</InputLabel>
            <Select
              displayEmpty
              renderValue={(value: unknown) => {
                if (isEmpty(value)) {
                  return <p className={styles.experimenterRequestDialogTextFieldFilledLabel}>Select</p>
                }
                return <>{getRoleLabel(value as UserRole)}</>
              }}
              onBlur={() => {
                validateField(role, 'role')
              }}
              value={role}
              onChange={e => setRole(e.target.value as UserRole)}
              label={'Role'}
              size="small">
              {map(Object.values(UserRole), option => (
                <MenuItem
                  key={option}
                  value={option}>
                  {getRoleLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {getEmptyFieldError('role')?.message && (
              <FormHelperText error>{getEmptyFieldError('role')?.message}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
    </CustomDialog>
  )
}

export default CreateUserModal
